import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { APIResponse } from '@/client/types/api';
import {
  BillingOverview,
  Invoice,
  InvoiceListParams,
  InvoicesListResponse,
  LedgerTransaction,
  normalizeInvoice,
  normalizeOrder,
  normalizePaymentMethod,
  normalizeRefundRequest,
  Order,
  OrderListParams,
  OrdersListResponse,
  Payment,
  PaymentListParams,
  PaymentMethod,
  PaymentMethodsListResponse,
  PaymentSource,
  PaymentSourcesResponse,
  PaymentsListResponse,
  RefundRequestListParams,
  RefundRequestsListResponse,
  Subscription,
  SubscriptionListParams,
  SubscriptionsListResponse,
  TransactionListParams,
  TransactionsListResponse,
  Wallet,
  WalletLedgerResponse,
  WalletListParams,
  WalletListResponse,
  WalletLookupResult,
  WalletPayoutConfig,
  WalletPayoutsResponse,
} from '@/client/types/payment';

const B = '/api/admin/v2';

/** ff-api wraps every payload in `{ status, data }` — always read `.data`. */
const unwrap = async <T>(promise: Promise<APIResponse<T> | T>): Promise<T> => {
  const res = await promise;
  if (res != null && typeof res === 'object' && 'data' in (res as object) && 'status' in (res as object)) {
    return (res as APIResponse<T>).data as T;
  }
  return res as T;
};

export const paymentKeys = {
  all: ['payments-v2'] as const,
  payments: () => [...paymentKeys.all, 'payments'] as const,
  paymentList: (p: object) => [...paymentKeys.payments(), 'list', p] as const,
  paymentDetail: (pid: number) => [...paymentKeys.payments(), 'detail', pid] as const,
  sources: () => [...paymentKeys.all, 'sources'] as const,
  paymentStatusCount: (status: string) => [...paymentKeys.payments(), 'count', status] as const,

  invoices: () => [...paymentKeys.all, 'invoices'] as const,
  invoiceList: (p: object) => [...paymentKeys.invoices(), 'list', p] as const,
  invoiceDetail: (invid: number) => [...paymentKeys.invoices(), 'detail', invid] as const,

  transactions: () => [...paymentKeys.all, 'transactions'] as const,
  transactionList: (p: object) => [...paymentKeys.transactions(), 'list', p] as const,
  transactionDetail: (tid: number) => [...paymentKeys.transactions(), 'detail', tid] as const,

  subs: () => [...paymentKeys.all, 'subs'] as const,
  subList: (p: object) => [...paymentKeys.subs(), 'list', p] as const,
  subDetail: (plid: number) => [...paymentKeys.subs(), 'detail', plid] as const,

  wallets: () => [...paymentKeys.all, 'wallets'] as const,
  wallet: (q: object) => [...paymentKeys.wallets(), q] as const,
  walletLedger: (wid: number, q: object) => [...paymentKeys.wallets(), 'ledger', wid, q] as const,

  orders: () => [...paymentKeys.all, 'orders'] as const,
  orderList: (p: object) => [...paymentKeys.orders(), 'list', p] as const,
  orderDetail: (orderid: number) => [...paymentKeys.orders(), 'detail', orderid] as const,
  refundRequests: (p: object) => [...paymentKeys.orders(), 'refund-requests', p] as const,

  cards: () => [...paymentKeys.all, 'cards'] as const,
  cardList: (p: object) => [...paymentKeys.cards(), 'list', p] as const,
};

const qs = (raw: Record<string, unknown>) => {
  const params = Object.entries(raw).reduce<Record<string, string>>((acc, [k, v]) => {
    if (v !== undefined && v !== null && v !== '') acc[k] = String(v);
    return acc;
  }, {});
  const s = new URLSearchParams(params).toString();
  return s ? `?${s}` : '';
};

// ─── Payments ─────────────────────────────────────────────────────────────────

export const usePaymentListQuery = (params: PaymentListParams = {}, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: paymentKeys.paymentList(params),
    queryFn: withErrorHandling(async (): Promise<PaymentsListResponse> => {
      return unwrap(
        request<APIResponse<PaymentsListResponse>>(`${B}/payments${qs(params as Record<string, unknown>)}`),
      );
    }, 'Failed to fetch payments'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const usePaymentStatusCountQuery = (status: string, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: paymentKeys.paymentStatusCount(status),
    queryFn: withErrorHandling(async () => {
      const res = await unwrap(
        request<APIResponse<PaymentsListResponse>>(
          `${B}/payments${qs({ status, limit: 1, offset: 0 })}`,
        ),
      );
      return res.pagination?.total ?? 0;
    }, 'Failed to count payments'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const usePaymentDetailQuery = (pid: number | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: paymentKeys.paymentDetail(pid ?? 0),
    queryFn: withErrorHandling(async (): Promise<Payment> => {
      return unwrap(request<APIResponse<Payment>>(`${B}/payments/${pid}`));
    }, 'Failed to fetch payment'),
    enabled: enabled && typeof pid === 'number' && pid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const usePaymentSourcesQuery = (enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: paymentKeys.sources(),
    queryFn: withErrorHandling(async (): Promise<PaymentSource[]> => {
      const raw = await unwrap(
        request<APIResponse<PaymentSourcesResponse>>(`${B}/payments/sources`),
      );
      return raw.sources ?? raw.payment_sources ?? [];
    }, 'Failed to fetch payment sources'),
    enabled,
    staleTime: DEFAULT_STALE_TIME * 12,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Invoices ─────────────────────────────────────────────────────────────────

export const useInvoiceListQuery = (params: InvoiceListParams = {}, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: paymentKeys.invoiceList(params),
    queryFn: withErrorHandling(async (): Promise<InvoicesListResponse> => {
      const raw = await unwrap(
        request<APIResponse<InvoicesListResponse>>(
          `${B}/invoices${qs(params as Record<string, unknown>)}`,
        ),
      );
      return {
        ...raw,
        invoices: (raw.invoices ?? []).map(normalizeInvoice),
      };
    }, 'Failed to fetch invoices'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useInvoiceDetailQuery = (invid: number | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: paymentKeys.invoiceDetail(invid ?? 0),
    queryFn: withErrorHandling(async (): Promise<Invoice> => {
      const raw = await unwrap(request<APIResponse<Invoice>>(`${B}/invoices/${invid}`));
      return normalizeInvoice(raw);
    }, 'Failed to fetch invoice'),
    enabled: enabled && typeof invid === 'number' && invid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Transactions (purchase ledger) ───────────────────────────────────────────

export const useTransactionListQuery = (params: TransactionListParams = {}, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: paymentKeys.transactionList(params),
    queryFn: withErrorHandling(async (): Promise<TransactionsListResponse> => {
      return unwrap(
        request<APIResponse<TransactionsListResponse>>(
          `${B}/transactions${qs(params as Record<string, unknown>)}`,
        ),
      );
    }, 'Failed to fetch transactions'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useTransactionDetailQuery = (tid: number | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: paymentKeys.transactionDetail(tid ?? 0),
    queryFn: withErrorHandling(async (): Promise<LedgerTransaction> => {
      return unwrap(request<APIResponse<LedgerTransaction>>(`${B}/transactions/${tid}`));
    }, 'Failed to fetch transaction'),
    enabled: enabled && typeof tid === 'number' && tid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Subscriptions ────────────────────────────────────────────────────────────

export const useSubscriptionListQuery = (params: SubscriptionListParams = {}, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: paymentKeys.subList(params),
    queryFn: withErrorHandling(async (): Promise<SubscriptionsListResponse> => {
      return unwrap(
        request<APIResponse<SubscriptionsListResponse>>(
          `${B}/subscriptions${qs(params as Record<string, unknown>)}`,
        ),
      );
    }, 'Failed to fetch subscriptions'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useSubscriptionDetailQuery = (plid: number | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: paymentKeys.subDetail(plid ?? 0),
    queryFn: withErrorHandling(async (): Promise<Subscription> => {
      return unwrap(request<APIResponse<Subscription>>(`${B}/subscriptions/${plid}`));
    }, 'Failed to fetch subscription'),
    enabled: enabled && typeof plid === 'number' && plid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Wallets ──────────────────────────────────────────────────────────────────

export const useWalletLookupQuery = (
  q: { uid?: number; oid?: number; ccode?: string; wid?: number },
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  const hasTarget = !!(q.wid || q.uid || q.oid);
  return useQuery({
    queryKey: paymentKeys.wallet(q),
    queryFn: withErrorHandling(async (): Promise<WalletLookupResult | null> => {
      if (q.wid) {
        const one = await unwrap(request<APIResponse<Wallet>>(`${B}/wallets/${q.wid}`));
        if (!one || (one as any).error) return null;
        return {
          wallets: [one],
          uid: one.uid,
          oid: one.oid,
          user_summary: one.user_summary,
          org_summary: one.org_summary,
          wid: one.wid,
        };
      }
      const raw = await unwrap(
        request<APIResponse<any>>(`${B}/wallets${qs(q as Record<string, unknown>)}`),
      );
      if (!raw || raw.error) return null;
      if (Array.isArray(raw.wallets)) {
        return {
          wallets: raw.wallets as Wallet[],
          uid: raw.uid ?? q.uid ?? null,
          oid: raw.oid ?? q.oid ?? null,
          user_summary: raw.user_summary,
          org_summary: raw.org_summary,
        };
      }
      if (raw.wid) {
        const one = raw as Wallet;
        return {
          wallets: [one],
          uid: one.uid,
          oid: one.oid,
          user_summary: one.user_summary,
          org_summary: one.org_summary,
          wid: one.wid,
        };
      }
      if (raw.wallet) {
        const one = raw.wallet as Wallet;
        return { wallets: [one], uid: one.uid, oid: one.oid };
      }
      return null;
    }, 'Failed to fetch wallet'),
    enabled: enabled && hasTarget,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** The Billing & Payments landing page: one read for every headline figure. */
export const useBillingOverviewQuery = (days: number, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: [...paymentKeys.all, 'overview', days] as const,
    queryFn: withErrorHandling(async (): Promise<BillingOverview> => {
      return unwrap(request<APIResponse<BillingOverview>>(`${B}/billing/overview?days=${days}`));
    }, 'Failed to fetch billing overview'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

/** Browse every wallet (richest first) with a per-currency summary. */
export const useWalletListQuery = (params: WalletListParams = {}, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: [...paymentKeys.wallets(), 'list', params] as const,
    queryFn: withErrorHandling(async (): Promise<WalletListResponse> => {
      return unwrap(
        request<APIResponse<WalletListResponse>>(
          `${B}/wallets${qs(params as Record<string, unknown>)}`,
        ),
      );
    }, 'Failed to fetch wallets'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

/** Owner withdrawals. Store payouts live on the store's own admin page. */
export const useWalletPayoutsQuery = (
  params: {
    status?: string;
    uid?: number;
    oid?: number;
    wid?: number;
    scope?: 'wallet' | 'store' | 'all';
    limit?: number;
    offset?: number;
  } = {},
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: [...paymentKeys.wallets(), 'payouts', params] as const,
    queryFn: withErrorHandling(async (): Promise<WalletPayoutsResponse> => {
      return unwrap(
        request<APIResponse<WalletPayoutsResponse>>(
          `${B}/wallets/payouts${qs(params as Record<string, unknown>)}`,
        ),
      );
    }, 'Failed to fetch withdrawals'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useWalletPayoutConfigQuery = (wid: number | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: [...paymentKeys.wallets(), 'payout-config', wid ?? 0] as const,
    queryFn: withErrorHandling(async (): Promise<WalletPayoutConfig | null> => {
      const data = await unwrap(
        request<APIResponse<{ config: WalletPayoutConfig | null }>>(
          `${B}/wallets/${wid}/payout-config`,
        ),
      );
      return data?.config ?? null;
    }, 'Failed to fetch payout details'),
    enabled: enabled && typeof wid === 'number' && wid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useWalletLedgerQuery = (
  wid: number | null,
  params: { type?: string; limit?: number; offset?: number } = {},
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: paymentKeys.walletLedger(wid ?? 0, params),
    queryFn: withErrorHandling(async (): Promise<WalletLedgerResponse> => {
      return unwrap(
        request<APIResponse<WalletLedgerResponse>>(
          `${B}/wallets/${wid}/transactions${qs(params as Record<string, unknown>)}`,
        ),
      );
    }, 'Failed to fetch wallet ledger'),
    enabled: enabled && typeof wid === 'number' && wid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

// ─── Orders ───────────────────────────────────────────────────────────────────

export const useOrderListQuery = (params: OrderListParams = {}, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: paymentKeys.orderList(params),
    queryFn: withErrorHandling(async (): Promise<OrdersListResponse> => {
      const raw = await unwrap(
        request<APIResponse<OrdersListResponse>>(
          `${B}/orders${qs(params as Record<string, unknown>)}`,
        ),
      );
      return {
        ...raw,
        orders: (raw.orders ?? []).map(normalizeOrder),
      };
    }, 'Failed to fetch orders'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useOrderDetailQuery = (orderid: number | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: paymentKeys.orderDetail(orderid ?? 0),
    queryFn: withErrorHandling(async (): Promise<Order> => {
      const raw = await unwrap(request<APIResponse<Order>>(`${B}/orders/${orderid}`));
      return normalizeOrder(raw);
    }, 'Failed to fetch order'),
    enabled: enabled && typeof orderid === 'number' && orderid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useRefundRequestListQuery = (params: RefundRequestListParams = {}, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: paymentKeys.refundRequests(params),
    queryFn: withErrorHandling(async (): Promise<RefundRequestsListResponse> => {
      const raw = await unwrap(
        request<APIResponse<RefundRequestsListResponse>>(
          `${B}/orders/refund-requests${qs(params as Record<string, unknown>)}`,
        ),
      );
      return {
        ...raw,
        refund_requests: (raw.refund_requests ?? []).map(normalizeRefundRequest),
      };
    }, 'Failed to fetch refund requests'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

// ─── Payment methods ──────────────────────────────────────────────────────────

export const usePaymentMethodsQuery = (
  q: { uid?: number; oid?: number; include_deleted?: boolean },
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  const hasTarget = !!(q.uid || q.oid);
  return useQuery({
    queryKey: paymentKeys.cardList(q),
    queryFn: withErrorHandling(async (): Promise<PaymentMethod[]> => {
      const raw = await unwrap(
        request<APIResponse<PaymentMethodsListResponse | PaymentMethod[]>>(
          `${B}/payment-methods${qs(q as Record<string, unknown>)}`,
        ),
      );
      if (Array.isArray(raw)) return raw.map(normalizePaymentMethod);
      return (raw.payment_methods ?? []).map(normalizePaymentMethod);
    }, 'Failed to fetch payment methods'),
    enabled: enabled && hasTarget,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};
